/* eslint-disable @nx/enforce-module-boundaries */
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Heading,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Select,
  Stack,
  Text,
} from '@mybridge/ui';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { forwardRef, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { UserCard } from 'v4/components/user-card';
import { JobsListingContext } from '../context';
import { JobPreferencesResumeUploader } from '../resume-uploader';
import { ApplyJobScreeningQuestion } from '../screening-question';
import { COUNTRY_CODE, EMPLOYMENT_CHOICES } from 'v4/lib/data';
import _ from 'lodash';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { useDirection } from '@mybridge/ui/custom-hooks/useDirection';
export const ApplyJobForm = forwardRef(({ ...props }, ref) => {
  const {
    jobDetails: job,
    applyDisc,
    appliedJob,
    appliedJobDetails,
    appliedJobDetailsFetching,
    refetchAppliedJobDetails,
    applyJob,
    applyJobIsLoading,
    jobPreferences,
    jobPreferencesResume
  } = useContext(JobsListingContext);
  const { t } = useTranslator();
  const direction = useDirection();
  const targetJob = appliedJobDetails ?? appliedJob ?? job ?? {};
  const { title } = targetJob;
  const { allConnectionIDs } = useSelector((state) => state.connection);
  const { hiring_company_details: company, is_resume_required } = targetJob;
  const { circle, followers } = allConnectionIDs ?? {};
  const { screening_question } = targetJob;
  const { userProfileInfo } = useSelector((state) => state?.userProfile);
  const [resumeInvalid, setResumeInvalid] = useState(false);
  const resumeSectionRef = useRef(null);

  const getInitialCountryIso = () => {
    if (!userProfileInfo) return 'US';
    
    const { mobile_country_code, country, iso_code } = userProfileInfo;
    
    if (iso_code) {
      const match = COUNTRY_CODE.find(
        (cc) => cc.iso?.toLowerCase() === iso_code?.toLowerCase()
      );
      if (match) return match.iso;
    }
    
    if (mobile_country_code) {
      const matches = COUNTRY_CODE.filter(
        (cc) => cc.code === String(mobile_country_code)
      );
      if (matches.length === 1) {
        return matches[0].iso;
      } else if (matches.length > 1) {
        const nameMatch = matches.find(
          (m) => m.country?.toLowerCase() === country?.toLowerCase()
        );
        if (nameMatch) return nameMatch.iso;
        
        if (String(mobile_country_code) === '1') {
          return 'US';
        }
        return matches[0].iso;
      }
    }
    
    if (country) {
      const nameMatch = COUNTRY_CODE.find(
        (cc) => cc.country?.toLowerCase() === country?.toLowerCase()
      );
      if (nameMatch) return nameMatch.iso;
    }
    
    return 'US';
  };

  const {
    handleSubmit,
    register,
    control,
    reset,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onChange',
  });

  useEffect(() => {
    if (userProfileInfo) {
      reset({
        email: userProfileInfo?.email || '',
        mobile_country_code: getInitialCountryIso(),
        mobile_number: userProfileInfo?.mobile_number || '',
      });
    }
  }, [userProfileInfo]);

  const isResumeValid = () => {
    const resData =
      jobPreferencesResume?.resume_first ||
      jobPreferencesResume?.resume_second ||
      jobPreferences?.resume_first ||
      jobPreferences?.resume_second;
    if (resData) {
      if (typeof resData === 'string') return resData.trim().length > 0;
      if (typeof resData === 'object') return true;
    }
    return false;
  };
  const { resume_first, resume_second } = jobPreferencesResume ?? jobPreferences ?? {};
  
  useEffect(() => {
    setResumeInvalid(false);
  }, [resume_first, resume_second]);

  const onSubmit = async (form) => {
    setResumeInvalid(false);

    if (is_resume_required && !isResumeValid()) {
      setResumeInvalid(true);
      resumeSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    const answerKeys = Object.keys(form ?? {})?.filter?.((k) =>
      k?.startsWith?.('__screening__')
    );

    const screening_response = _.cloneDeep(screening_question ?? []);
    if (answerKeys?.length && screening_response?.length) {
      for (let sr of screening_response) {
        const placeholderReplacementRegexStr = `\\[${
          sr.questions[0]?.ideal_answer_text ?? '.+'
        }\\]`;
        if (answerKeys?.includes('__screening__' + normalizeName(sr?.name))) {
          if (Array.isArray(sr.questions)) {
            sr.questions[0] = {
              ...(sr.questions[0] ?? {}),
              response: form['__screening__' + normalizeName(sr.name)],
              formatted_question: sr.questions[0]?.question?.replaceAll?.(
                new RegExp(placeholderReplacementRegexStr, 'g'),
                sr.questions[0]?.place_holder_value ?? ''
              ),
            };
            // console.log('transformedScreeningQuestions', sr.questions[0]);
          } else {
            sr.questions = {
              ...(sr.questions ?? {}),
              response: form['__screening__' + normalizeName(sr.name)],
              formatted_question: sr.questions?.question?.replaceAll?.(
                new RegExp(placeholderReplacementRegexStr, 'g'),
                sr.questions?.place_holder_value ?? ''
              ),
            };
          }
        }
      }
    }

    const normalizedArray = _.map(screening_response, (item) => {
      // Check if 'questions' is an object, convert it to an array
      if (_.isObject(item.questions) && !_.isArray(item.questions)) {
        return {
          ...item,
          questions: [item.questions],
        };
      }
      return item;
    });

    const { email, mobile_number, mobile_country_code } = form ?? {};
    const selectedCountry = COUNTRY_CODE.find(cc => cc.iso === mobile_country_code);
    const numericCountryCode = selectedCountry ? selectedCountry.code : mobile_country_code;
    const payload = {
      email,
      mobile_country_code: numericCountryCode,
      mobile_number,
      jobs_id: appliedJobDetails?.id,
      candidate_id: userProfileInfo?.id,
      screening_response: normalizedArray,
      job_preferences: jobPreferences,
    };
    // console.log('onsubmit ', normalizedArray)
    await applyJob(payload);
  };

  const transformedScreeningQuestions = useMemo(
    () =>
      screening_question?.map?.((sq) => {
        if (sq?.name?.toLowerCase?.() === 'work authorization') {
          const sq_ = _.cloneDeep(sq);
          if (sq_?.questions?.length) {
            sq_.questions[0].ideal_answer_value = 'No';
          } else {
            sq_.questions.ideal_answer_value = 'No';
          }
          return sq_;
        }
        if (sq?.name?.toLowerCase?.() === 'language') {
          const sq_ = _.cloneDeep(sq);
          sq_.questions = Array.isArray(sq_.questions)
            ? sq_?.questions
            : [sq_?.questions ?? sq_?.question];
          sq_.questions[0].ideal_answer_type = 'select';
          sq_.questions[0].ideal_answer_values = [
            {
              title: 'Basic',
              value: 'basic',
            },
            {
              title: 'Fluent',
              value: 'fluent',
            },
            {
              title: 'Conversational',
              value: 'conversational',
            },
          ];

          return sq_;
        }
        return sq;
      })?.filter?.((sq) => {
        if (sq?.name?.toLowerCase?.() === 'resume') {
          if (!is_resume_required || isResumeValid()) {
            return false;
          }
        }
        return true;
      }),
    [screening_question, is_resume_required, jobPreferences, jobPreferencesResume]
  );

  const normalizeName = (name) => {
    return name?.replaceAll?.(/([^a-zA-Z0-9]+)/g, '') ?? name;
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <hr />

      <Stack mt={3}>
        <Heading size="sm">{t('pages.contactInfo') || "Contact Info"}</Heading>
        <Stack>
          <UserCard user={userProfileInfo} />
        </Stack>
        <Stack>
          {/*Email Address Details */}
          <HStack>
            <FormControl
              w={['full', 'full', '70%']}
              as={HStack}
              flex={1}
              isInvalid={errors?.email}
            >
              <FormLabel flexShrink={0}>{t('createPage.emailAddress') || "Email Address"}*</FormLabel>
              <Input
                defaultValue={userProfileInfo?.email}
                {...register('email', { required: true })}
                type="email"
                fontSize={['xs', 'md']}
                variant="whiteInput"
              />
              <FormErrorMessage>{t('createJob.emailIsRequired') || "Email is required"}</FormErrorMessage>
            </FormControl>
            <Box flex={1} display={['none', 'none', 'block']} />
          </HStack>

          {/*Mobile Number Details */}
          <HStack alignItems="flex-start" flexDir={['column', 'column', 'row']} gap={[2, 6]} w="full">
            <FormControl
              flex={1}
              w="full"
              isInvalid={errors?.mobile_country_code}
            >
              <HStack spacing={3} w="full">
                <FormLabel flexShrink={0} m={0}>{t('createJob.phoneCountryCode') || "Phone country code"}*</FormLabel>
                <Controller
                  name="mobile_country_code"
                  control={control}
                  defaultValue={getInitialCountryIso()}
                  rules={{ required: true }}
                  render={({ field: { value, onChange } }) => {
                    const selected = COUNTRY_CODE.find((cc) => cc.iso === value) || COUNTRY_CODE.find((cc) => cc.iso === 'US') || COUNTRY_CODE[0];
                    return (
                      <Menu matchWidth placement="bottom-start">
                        <MenuButton
                          as={Button}
                          border="1px solid"
                          borderColor="brandGray.500"
                          bg="white"
                          borderRadius="5px"
                          rightIcon={<ChevronDownIcon />}
                          fontSize={['xs', 'md']}
                          fontWeight="normal"
                          textAlign="left"
                          flex={1}
                          w="full"
                          h="48px"
                          px={3}
                          overflow="hidden"
                          justifyContent="space-between"
                          display="flex"
                          alignItems="center"
                          _hover={{ borderColor: 'brandGray.700' }}
                          _active={{ bg: 'white' }}
                        >
                          <Text noOfLines={1} fontSize={['xs', 'md']}>
                            {selected ? `(+${selected.code}) ${selected.country}` : 'Select Country'}
                          </Text>
                        </MenuButton>
                        <Portal>
                          <MenuList minW="240px" maxH="250px" overflowY="auto" zIndex={1500}>
                            {COUNTRY_CODE?.map((cc, ccIndex) => (
                              <MenuItem
                                key={ccIndex}
                                onClick={() => onChange(cc.iso)}
                                fontSize="sm"
                                bg={value === cc.iso ? 'gray.100' : 'transparent'}
                              >
                                {`(+${cc.code}) ${cc.country}`}
                              </MenuItem>
                            ))}
                          </MenuList>
                        </Portal>
                      </Menu>
                    );
                  }}
                />
              </HStack>
              <FormErrorMessage>{t('createJob.countryCodeIsRequired') || "Country code is required"}</FormErrorMessage>
            </FormControl>

            <FormControl
              flex={1}
              w="full"
              isInvalid={errors?.mobile_number}
            >
              <HStack spacing={3} w="full">
                <FormLabel {...(direction === 'rtl') ? {mr: '10px'} : {}} flexShrink={0} m={0}>{t('createJob.mobilePhoneNumber') || "Mobile phone number"}</FormLabel>
                <Input
                  defaultValue={userProfileInfo?.mobile_number}
                  {...register('mobile_number', {
                    required: false,
                    minLength: 10,
                    maxLength: 10,
                    pattern: /^[0-9]*$/,
                  })}
                  placeholder={t('profileDetails.mobileNumber') || "Mobile number"}
                  variant="whiteInput"
                  type="number"
                  fontSize={['xs', 'md']}
                  flex={1}
                  onKeyDown={(e) => {
                    if (['e', 'E', '+', '-'].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                />
              </HStack>
              <FormErrorMessage>
              {t('createJob.phoneNumberRequired') || "Phone number is required (10 digits)"}
              </FormErrorMessage>
            </FormControl>
          </HStack>
        </Stack>

        <Stack mt={3} ref={resumeSectionRef}>
          <FormControl isInvalid={resumeInvalid}>
            <Heading size="sm">{t('jobs.resume') || "Resume"}</Heading>
            {/* {is_resume_required && !isResumeValid() && (
              <Text>{t('createJob.includeUpdatedResume') || "Be sure to include an updated resume"}*</Text>
            )} */}
            <JobPreferencesResumeUploader />
            <FormErrorMessage>{t('createJob.resumeIsRequired') || "Resume is required"}{is_resume_required ? '*' : ''}</FormErrorMessage>
          </FormControl>
        </Stack>

        {transformedScreeningQuestions?.length ? (
          <>
            <hr />
            <Stack mt={3} mb={3}>
              <Heading size="sm">{t('createJob.screeningQuestions') || "Screening questions"}</Heading>
            </Stack>
            {transformedScreeningQuestions?.map?.((sq, sqIndex) => {
              return (
                <FormControl
                  key={sqIndex}
                  isInvalid={
                    errors?.['__screening__' + normalizeName(sq?.name)]
                  }
                >
                  <Controller
                    render={({ field }) => (
                      <ApplyJobScreeningQuestion
                        {...field}
                        index={sqIndex}
                        question={sq}
                      />
                    )}
                    defaultValue={
                      (sq?.questions?.[0]?.is_must_have ?? sq?.questions?.is_must_have)
                        ? ""
                        : (sq?.questions?.[0]?.ideal_answer_value ?? sq?.questions?.ideal_answer_value ?? "")
                    }
                    name={'__screening__' + normalizeName(sq?.name)}
                    control={control}
                    rules={{
                      required:
                        sq?.questions?.[0]?.is_must_have ??
                        sq?.questions?.is_must_have,
                    }}
                  />
                  <FormErrorMessage>{t('createJob.thisFieldIsRequired') || "This field is required"}</FormErrorMessage>
                </FormControl>
              );
            })}
          </>
        ) : (
          ''
        )}

        <hr />

        <Stack direction={['column', 'row']} py={4} flex={1}>
          <HStack flex={1}>
            <Text fontSize="xs">
              {t('createJob.autoSaveAnswers') || "We will automatically save your answers and resume to pre-fill future applications and improve your experience."}
            </Text>
          </HStack>
          <HStack justifyContent="flex-end" flex={1}>
            <Button borderRadius="full" onClick={(e) => applyDisc?.onClose?.()} variant="secondary">
              {t('buttons.back') || "Back"}
            </Button>
            <Button
              borderRadius="full"
              type="submit"
              variant="primary"
              isLoading={applyJobIsLoading}
              isDisabled={Boolean(is_resume_required && !isResumeValid())}
            >
              {t('createJob.submitApplication') || "Submit Application"}
            </Button>
          </HStack>
        </Stack>
      </Stack>
    </form>
  );
});
